// 업데이트 소식을 앱 전체가 **한 번만** 불러 나눠 쓰게 하는 저장소.
//
// 소식을 보는 곳이 둘이다 — 머리말의 뱃지와, 쓰는 도중 뜨는 알림. 각자 불러오게 두면 화면을
// 열 때마다 같은 요청이 두 번 나가고, 둘이 서로 다른 시점의 목록을 들고 있게 된다.
// 그래서 모듈 하나에 담아 두고 구독만 나눠 준다(Context 배선 없이도 앱 어디서나 같은 값).
//
// 언제 다시 불러오나:
//   · 처음 붙을 때 한 번
//   · 다른 탭·앱을 보다 **돌아왔을 때**(가장 흔한 "그새 배포됐나" 순간이다)
//   · 30분마다 — 하루 종일 한 화면을 켜 두고 쓰는 사람이 있다
// 실패는 조용히 넘긴다. 소식이 안 뜨는 건 사용자가 하려던 일과 무관하다.

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { fetchWhatsNew, lastSeenAt, markSeen, seedSeen, unreadCount, type WhatsNewItem } from "../lib/whatsNew";

type State = {
  items: WhatsNewItem[];
  latest: string | null;
  loading: boolean;
  /** 이 세션에서 처음 확인한 최신 시각 — 이보다 새 게 오면 "쓰는 도중 도착한 소식"이다. */
  sessionBaseline: string | null;
  /** 안 읽음 계산에 쓰는 값. localStorage 는 변화를 알려주지 않으므로 여기 복제해 둔다. */
  seenAt: string | null;
};

let state: State = { items: [], latest: null, loading: true, sessionBaseline: null, seenAt: lastSeenAt() };
const listeners = new Set<() => void>();

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  for (const fn of listeners) fn();
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

let inFlight: Promise<void> | null = null;
let lastFetchedAt = 0;

export function refreshWhatsNew(force = false): Promise<void> {
  // 화면을 오갈 때마다 부르지 않는다 — 5분 안에 다시 물어봐야 답이 같다.
  if (!force && Date.now() - lastFetchedAt < 5 * 60 * 1000) return Promise.resolve();
  inFlight ??= fetchWhatsNew()
    .then((feed) => {
      lastFetchedAt = Date.now();
      // 처음 온 사람은 지난 소식을 전부 "본 것"으로 친다(빨간 뱃지 20개로 맞이하지 않는다).
      seedSeen(feed.latestPublishedAt);
      set({
        items: feed.items,
        latest: feed.latestPublishedAt,
        loading: false,
        seenAt: lastSeenAt(),
        sessionBaseline: state.sessionBaseline ?? feed.latestPublishedAt,
      });
    })
    .catch(() => set({ loading: false }))
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

let wired = false;

/** 다시 불러올 시점을 앱 전체에 한 번만 걸어 둔다. */
function wire() {
  if (wired || typeof document === "undefined") return;
  wired = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refreshWhatsNew();
  });
  setInterval(() => refreshWhatsNew(), 30 * 60 * 1000);
}

export function useWhatsNew() {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );

  useEffect(() => {
    wire();
    refreshWhatsNew();
  }, []);

  const unread = unreadCount(snapshot.items);

  /** 목록을 열어 봤다 → 지금까지의 소식을 전부 본 것으로 도장. */
  const markAllSeen = useCallback(() => {
    markSeen(state.latest);
    set({ seenAt: lastSeenAt(), sessionBaseline: state.latest });
  }, []);

  return {
    items: snapshot.items,
    loading: snapshot.loading,
    unread,
    /** 쓰는 도중에 새로 도착한 소식(알림 띄울 거리). 세션 시작 시점엔 항상 없다. */
    arrived:
      snapshot.sessionBaseline && snapshot.latest && snapshot.latest > snapshot.sessionBaseline
        ? snapshot.items.filter((i) => i.publishedAt > (snapshot.sessionBaseline as string))
        : [],
    markAllSeen,
    refresh: () => refreshWhatsNew(true),
  };
}
